import React, { useEffect, useState } from 'react'
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  DragOverlay,
  closestCenter,
} from '@dnd-kit/core'
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities'
import { Card, message, Table } from 'antd'
import { TApiProjectSection, TApiTaskDetails } from '@timenotes/shared/src/services/api-client/types'
import { useQueryClient } from 'react-query'
import { useProjectBoard } from '@timenotes/shared/src/features/project-board'
import { cloneDeep, flatMap, keyBy, orderBy } from 'lodash'
import useReorderProjectSectionMutation from '@timenotes/shared/src/services/project-sections/queries/use-reorder-project-section-mutation'
import { useUpdateTaskPositionMutation } from '@timenotes/shared/src/services/tasks/queries/use-update-task-position-mutation'
import { useInvalidateOtherProjectSections } from '@timenotes/shared/src/services/project-sections/utils/use-invalidate-other-project-sections'


interface SectionProps {
  id: string;
  children?: React.ReactNode | React.ReactNode[]
}

// Draggable table row component
export const DraggableBodyRow = (props: any) => {
  const { className, style, ...restProps } = props
  const { 'data-row-key': dataRowKey } = props as any

  // Use useSortable to make the row draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: dataRowKey 
  })

  const mergedStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'pointer',
    outline: 'none',
    ...(isDragging ? { opacity: 0.5 } : {}),
  };

  return (
    <tr
      {...restProps}
      ref={setNodeRef}
      style={mergedStyle}
      {...attributes}
      {...listeners}
    />
  );
};

// SortableSection component making the section draggable
export const SortableSection = (props: SectionProps) => {
  const {
    id,
  } = props

  // Use useSortable to make the section draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    opacity: isDragging ? 0.5 : undefined,
    marginBottom: '16px',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      { props.children }
    </div>
  )
}

interface DraggableSectionsProps {
  projectId: string
  projectSections: TApiProjectSection[]
  children?: React.ReactNode | React.ReactNode[]
}

const DraggableSections = (props: DraggableSectionsProps) => {
  // State representing sections and their items
  const {
    projectId,
    projectSections
  } = props

  const [activeTaskInitialSectionId, setActiveTaskInitialSectionId] = useState<string | undefined>(undefined)

  // query client data
  const queryClient = useQueryClient()

  const [dragDebugText, setDragDebugText] = useState("")

  const {
    tasksFiltersFormik,
    setIsDraggingSection,
  } = useProjectBoard()

  const tasksFilters = tasksFiltersFormik.values

  const invalidateOtherProjectSections = useInvalidateOtherProjectSections(projectId, tasksFilters)

  const sectionsMap = keyBy(projectSections, 'id')
  const tasksMap = keyBy(flatMap(projectSections, (section) => section.tasks.tasks), 'id')

  const getTasksForSection = (sectionId: string) => {
    const section = sectionsMap[sectionId]
    if (!section) throw `Section with id ${sectionId} not found in the dictionary!`

    return section.tasks.tasks
  }

  const getTaskById = (taskId: string): TApiTaskDetails | undefined => {
    const task = tasksMap[taskId]
    if (!task) return undefined //throw `Task with id ${taskId} not found in the dictionary!`
    return task
  }

  // State to track the active item being dragged
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<'section' | 'item' | null>(null);

  // State to keep track of the sections before dragging starts
  const [projectSectionsBackup, setProjectSectionsBackup] = useState<TApiProjectSection[] | null>(null)

  const restoreProjectSectionsBackup = () => {
    if (projectSectionsBackup != null) {
      queryClient.setQueryData(['project-sections', projectId, tasksFilters], (old) => {
        return {
          projectSections: projectSectionsBackup
        }
      })
    }
  }

  const updateTaskPositionMutation = useUpdateTaskPositionMutation({
    projectId: projectId,
  }, {
    onSettled: () => {
      setProjectSectionsBackup(null)
      invalidateOtherProjectSections()
    },
    onError: () => {
      message.error("Something went wrong!")
      restoreProjectSectionsBackup()
      setProjectSectionsBackup(null)
    }
  })

  const reorderProjectSectionMutation = useReorderProjectSectionMutation({
    onSettled: () => {
      setProjectSectionsBackup(null)
    },
    onError: () => {
      message.error("Something went wrong!")
      restoreProjectSectionsBackup()
      setProjectSectionsBackup(null)
    }
  })

  // Set up sensors for dragging (mouse/touch and keyboard)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Adding drag distance needed to detect the drag allows all the elements to respond
      // to click handlers when user is not actually draging but just clicking
      activationConstraint: {
        distance: 8,
      }
    }),
  );

  // Helper function to find which section an item belongs to
  const findTaskSectionId = (id: string): string | null => {
    if (projectSections.find((section) => section.id === id)) {
      return id
    }

    for (const section of projectSections) {
      if (getTasksForSection(section.id).find((item) => item.id === id)) {
        return section.id
      }
    }

    return null
  }

  // Handler called when dragging starts
  const handleDragStart = ({ active }: DragStartEvent) => {
    // Store Active resource id
    const activeId = active.id as string
    setActiveId(activeId)

    // Store active task initial section id to allow the reset
    if (props.children) {
      const activeTask = getTaskById(`${activeId}`)
      if (activeTask) {
        setActiveTaskInitialSectionId(activeTask.projectSectionId)
      }
    }

    // Determine if the active item is a section or an item
    const isSection = projectSections.some((section) => section.id === activeId)
    const newActiveType = isSection ? 'section' : 'item'

    // Store active draggable resource type
    setActiveType(newActiveType)

    // Handle side effects of sections dragging
    if (newActiveType == 'section') {
      // Delay in order to ensure drag placeholder position is in the place of mouse
      // dragging, not moved by changes in the UI outside
      setTimeout(() => setIsDraggingSection(true), 100)

      // Scrolling top due to potential long scroll
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // Optional: Adds smooth scrolling
      })
    }

    setProjectSectionsBackup(projectSections)
  };

  // Handler called when an item is dragged over another droppable area
  // It is handling only changing the active section container, visual sorting stuff
  // happens directly via sorting context
  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    /* Not needed as handled automatically by Sorting context of the DNDKit
    if (activeType === 'section') {
      /...
    */

     if (activeType === 'item') {
      // Dragging an item
      const initialSectionId = findTaskSectionId(activeId)
      const overSectionId = findTaskSectionId(overId)

      if (!initialSectionId|| !overSectionId) return

      const activeTask = getTaskById(activeId) as TApiTaskDetails
      const overTask = getTaskById(overId) as TApiTaskDetails

       // If not draggin over the task, fuck this shit for now
      if (!overTask) {
         const overSectionId = `${findTaskSectionId(overId)}`

         const section = sectionsMap[overSectionId]

         // Just return if dragging over nor section nor task
         if (section == undefined) return

         const isEmptySection = section.tasks.tasks.length == 0
         setDragDebugText(`Dragging over section. Empty? - ${isEmptySection}`)

         if (initialSectionId != overSectionId && isEmptySection) {

           // Handle moving task to the section ( not by task )
           queryClient.setQueryData(['project-sections', projectId, tasksFilters], (old) => {
             const newData = cloneDeep(old) as any

             // Find the new task section and add task to a new section locally
             const newProjectSections = newData.projectSections as TApiProjectSection[]
             const newSection = newProjectSections.find((section) => section.id == overSectionId) as TApiProjectSection

             const taskInNewSection = cloneDeep(activeTask)

             // Add active task as the only task in this section
             newSection.tasks.tasks = [taskInNewSection]
             newSection.tasks.meta.pagination.totalCount += 1

             // Remove task from the old section locally
             const oldSection = newProjectSections.find((section) => section.id == initialSectionId) as TApiProjectSection
             oldSection.tasks.tasks = oldSection?.tasks.tasks.filter((t) => t.id != activeId)

             // Decrease total tasks count of old section
             oldSection.tasks.meta.pagination.totalCount -= 1

             // Return new section
             newData.projectSections = newProjectSections
             return newData
           })

         }

         // Stop processing below as active is not a task
         return
      }

      if (overTask.name) {
        setDragDebugText(`Dragging over - ${overTask.name} with position ${overTask.projectSectionPosition}`)
      }

      // Not needed as handled automatically by Sorting context of the DNDKit
      if (initialSectionId == overSectionId) {
        // ...
      }

      // Handle moving task to other section
      if (initialSectionId !== overSectionId) {
        //message.success(`I MOVED TO NEW SECTION ONLY NOW - position ${overTask.projectSectionPosition}`)

        queryClient.setQueryData(['project-sections', projectId, tasksFilters], (old) => {
          const newData = cloneDeep(old) as any

          // Find the new task section and add task to a new section locally
          const newProjectSections = newData.projectSections as TApiProjectSection[]
          const newSection = newProjectSections.find((section) => section.id == overSectionId) as TApiProjectSection

          const taskInNewSection = cloneDeep(activeTask)

          // Check if visually task should be added as a task on top of the list or below the task on top user drags
          const overTaskIndex = newSection.tasks.tasks.findIndex((task) => task.id == overId)
          const shouldAddBelow = overTaskIndex == 0 ? 0 : 1
          newSection.tasks.tasks.splice(overTaskIndex + shouldAddBelow, 0, taskInNewSection)
          newSection.tasks.meta.pagination.totalCount += 1

          // Remove task from the old section locally
          const oldSection = newProjectSections.find((section) => section.id == initialSectionId) as TApiProjectSection
          oldSection.tasks.tasks = oldSection?.tasks.tasks.filter((t) => t.id != activeId)

          // Decrease total tasks count of old section
          oldSection.tasks.meta.pagination.totalCount -= 1

          // Return new section
          newData.projectSections = newProjectSections
          return newData
        })
      }
    }
  };

  // Handler called when dragging ends
  const handleDragEnd = ({ active, over }: DragEndEvent) => {

    if (!over) {
      // Drag was canceled or dropped outside droppable area
      // trigger cancel scenario and return
      handleDragCancel()
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    if (activeType === 'section') {
      // Dropping a section

      const overSectionId = findTaskSectionId(overId)

      let newPosition: number
      if (activeId !== overId) {

        queryClient.setQueryData(['project-sections', projectId, tasksFilters], (old) => {
          const newData = cloneDeep(old) as any
          const newProjectSections = newData.projectSections as TApiProjectSection[]
        
          // Get the dragged and target sections
          const activeSection = newProjectSections.find((section) => section.id === activeId) as TApiProjectSection
          const overSection = newProjectSections.find((section) => section.id === overSectionId) as TApiProjectSection
        
          if (!activeSection || !overSection) {
            throw "Section not found!"
          }
        
          const activePosition = activeSection.position
          const overPosition = overSection.position
        
          // Determine if we're moving up or down in the list
          const movingUp = activePosition > overPosition
        
          // Adjust positions of sections
          for (const section of newProjectSections) {
        
            // Moving up: shift down sections between new and old position
            if (movingUp && section.position >= overPosition && section.position < activePosition) {
              section.position += 1
            }
        
            // Moving down: shift up sections between old and new position
            if (!movingUp && section.position <= overPosition && section.position > activePosition) {
              section.position -= 1
            }
          }
        
          // Assign the new position to the dragged section
          activeSection.position = overPosition

          // TODO: move everything to react query layer?
          reorderProjectSectionMutation.mutate({
            projectId: activeSection.projectId,
            sectionId: activeSection.id,
            position: activeSection.position,
          })

          // Sort the sections by position to reflect a new order
          newData.projectSections = orderBy(newProjectSections, (s) => s.position)
              
          return newData
        })
      }

    } else if (activeType === 'item') {
      // Dropping a task
      const overSectionId = findTaskSectionId(overId)

      if (!activeTaskInitialSectionId || !overSectionId) {
        // If somehow cant find a section for a given task 
        // no dropping and treat is as cancel
        console.warn("Cancelling a task drop due to no droppable section found!")
        handleDragCancel()
        return
      }

      // Update task position within the same section
      if (activeTaskInitialSectionId === overSectionId || true) {
        // by default new position is just 0 -> e.g. empty section container
        let newPosition = 0

        // if drop is over a container and its a task found
        // update the task position based on based on the drop/over task position
        // which can be e.g. 1, 3, 80 on the list due to archived tasks there, so it 
        // needs to be relative to over task position

        if (overId) {
          const overTask = getTaskById(overId)

          if (overTask) {
            newPosition = overTask.projectSectionPosition
          } 
        }

        // Check if over is a section
        const overSection = sectionsMap[overId]

        // Check if over is a section and if its not empty
        if (!!overSection && overSection.tasks.tasks.length > 0) {
          // Do not process dropping if over is a not empty section and 
          // user tries to drop a task directly on top of section placeholder
          // to avoid figuring out position based on the empty section space
          // --> only allow dropping a task directly on top of other task in this case
          return
        }

        queryClient.setQueryData(['project-sections', projectId, tasksFilters], (old) => {

          // Put the task in the rigth place in the project section tasks array
          const newData = cloneDeep(old) as any
          const newProjectSections = newData.projectSections as TApiProjectSection[]

          // Add task to a new section locally
          const section = newProjectSections.find((section) => section.id == overSectionId) as TApiProjectSection
          const tasks = section.tasks.tasks

          const activeIndex = tasks.findIndex((t) => t.id == activeId)
          const overIndex = tasks.findIndex((t) => t.id == overId)

          const newTasks = moveItem(tasks, activeIndex, overIndex)

          const overTask = getTaskById(overId)
          const activeTask = getTaskById(activeId)

          if (!activeTask) {
            throw "Active task not found!"
          }

          let newTaskPosition = overTask ? overTask.projectSectionPosition : 0

          if (overSectionId == activeTaskInitialSectionId) {
            const initialTaskPosition = activeTask.projectSectionPosition

            // Check if we are moving up or down in the array
            // moving up means lower task position
            const movingUp = newTaskPosition < initialTaskPosition

            for (const task of newTasks) {
              if (task.id === activeTask.id) {
                // Update the drop task
                task.projectSectionPosition = newTaskPosition

                // Skip the active task from shifting
                continue
              }

              // Moving up: tasks between the new and initial position need to shift down
              if (movingUp && task.projectSectionPosition >= newTaskPosition && task.projectSectionPosition < initialTaskPosition) {
                task.projectSectionPosition += 1
              }

              // Moving down: tasks between the initial and new position need to shift up
              if (!movingUp && task.projectSectionPosition <= newTaskPosition && task.projectSectionPosition > initialTaskPosition) {
                task.projectSectionPosition -= 1
              }
            }
          } else {
            // INSERT TASK TO A NEW SECTION

            if (overId === activeId) {
              // Insert at the top or bottom of the new section
              // message.success(`Dropping on my own (index: ${activeIndex})`)
              if (activeIndex === 0) {
                // Insert at the top of the new section
                // Locally a dropped task is already there so we look for the first one below it
                // and takes it position. If does not exists we take 0

                const firstTaskExceptNewOne = newTasks[1]
                newTaskPosition = firstTaskExceptNewOne ? firstTaskExceptNewOne.projectSectionPosition : 0

                // message.success(`New task position ${newTaskPosition}`)

                // Shift all tasks in the new section down by 1 to make space at the top
                for (const task of newTasks) {
                  if (task.id == activeId) continue

                  if (task.projectSectionPosition >= newTaskPosition) {
                    task.projectSectionPosition += 1
                  }
                }
              } else {
                // Insert at the bottom of the new section
                newTaskPosition = Math.max(...newTasks.map(t => t.projectSectionPosition)) + 1
                // No shift needed as it's added to the end
              }
            } else {
              // Insert in-between tasks in the new section, relative to overTask
              newTaskPosition = overTask ? overTask.projectSectionPosition : 0

              // Determine if inserting above or below overTask
              const insertingAbove = activeIndex != 0

              if (insertingAbove) {
                // Insert above overTask: shift tasks from newTaskPosition and onward down
                for (const task of newTasks) {
                  if (task.id == activeId) continue
                  if (task.projectSectionPosition >= newTaskPosition) {
                    task.projectSectionPosition += 1
                  }
                }
              } else {
                // Insert below overTask: increment newTaskPosition and shift tasks below it
                newTaskPosition += 1
                for (const task of newTasks) {
                  if (task.id == activeId) continue

                  if (task.projectSectionPosition >= newTaskPosition) {
                    task.projectSectionPosition += 1
                  }
                }
              }
            }

            const taskInNewSection = newTasks.find((t) => t.id == activeId) as TApiTaskDetails

            taskInNewSection.projectSectionId = overSectionId
            taskInNewSection.projectSectionPosition = newTaskPosition

            // message.success(`New task position again! ${newTaskPosition}`)

            // For all tasks in initial dragged task section which were on higher position then the
            // removed task -> decrease position by 1
            const oldSection = newProjectSections.find((section) => section.id == activeTaskInitialSectionId) as TApiProjectSection
            for (const task of oldSection.tasks.tasks) {
              if (task.projectSectionPosition > activeTask.projectSectionPosition) {
                task.projectSectionPosition -= 1
              }
            }

          }

          updateTaskPositionMutation.mutate({
            taskId: activeId as string,
            projectSectionId: overSectionId as string,
            projectSectionPosition: newTaskPosition as number,
          })

          section.tasks.tasks = newTasks

          return newData
        })

      }
    }

    // Reset activeId and clear previousSections after the drag ends
    setActiveId(null)
    setIsDraggingSection(false)
    setActiveType(null)
    setProjectSectionsBackup(null)
  }

  // Handler called when the drag is canceled (e.g., by pressing Escape)
  const handleDragCancel = () => {
    // Restore the sections to their previous state
    restoreProjectSectionsBackup()

    setActiveId(null)
    setActiveType(null)
    setIsDraggingSection(false)
    setActiveTaskInitialSectionId(undefined)
    setProjectSectionsBackup(null)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart} // Attach drag start handler
      onDragOver={handleDragOver} // Attach drag over handler
      onDragEnd={handleDragEnd} // Attach drag end handler
      onDragCancel={handleDragCancel} // Attach drag cancel handler
      autoScroll={{
        layoutShiftCompensation: false
      }}
    >
      { props.children }

      {/* Overlay displayed during dragging */}
      <DragOverlay
        dropAnimation={null}
        style={{
          zIndex: 10000,
        }}
      >
        {activeId && activeType === 'section' ? (
          <Card>
            <span>{sectionsMap[activeId]?.name}</span>
          </Card>
        ) : activeId && activeType === 'item' ? (
          <Card>
            {
              // Display the text of the active item
              getTaskById(activeId)?.name
            }
            { false && dragDebugText && (
              // TODO: remove debugging 
                <span style={{ marginLeft: '20px' }}>{ dragDebugText }</span>
            )}
            
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

function moveItem<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex) return array // No need to move if indices are the same

  // Remove the item from the original position
  const [item] = array.splice(fromIndex, 1)

  // Insert the item at the new position
  array.splice(toIndex, 0, item)

  return array // Returning the modified array for convenience
}

export default DraggableSections